"use client";

import type {
  FilterData,
  GetResponse,
  HomepageCategory,
  HomepageSlide,
  LinkBlock,
  MenuItem,
  Pagination,
  PopularCategory,
  PopularSearch,
  PopularSearchCategory,
  Product,
  ProductGroup,
  SearchFilters,
  SortOrderFormatted,
} from "@workspace/ui/types";
import type { CarryShop } from "@workspace/ui/types";
import type { Dispatch } from "react";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";

import {
  CarryShopsAPI,
  ContactFormAPI,
  GetResponseAPI,
  HomePageAPI,
  PopularSearchesAPI,
} from "@/const/endPoint";
import { buildSearchUrl, snakeToCamelCaseObject } from "@/utils";
import axiosInstance from "@/utils/api/fetchData/axiosInstance";

const CORRECT_PASSWORD = "ayx*WKR_eqx-tbv4dve";
const AUTH_KEY = "app-authenticated";

export enum GlobalActionType {
  // Simple auth
  START_SIMPLE_AUTH = "START_SIMPLE_AUTH",
  SUCCESS_SIMPLE_AUTH = "SUCCESS_SIMPLE_AUTH",
  FAILURE_SIMPLE_AUTH = "FAILURE_SIMPLE_AUTH",
  LOGOUT = "LOGOUT",

  // Homepage
  START_HOMEPAGE = "START_HOMEPAGE",
  SUCCESS_HOMEPAGE = "SUCCESS_HOMEPAGE",
  FAILURE_HOMEPAGE = "FAILURE_HOMEPAGE",

  // Carry Shops
  START_CARRY_SHOPS = "START_CARRY_SHOPS",
  SUCCESS_CARRY_SHOPS = "SUCCESS_CARRY_SHOPS",
  FAILURE_CARRY_SHOPS = "FAILURE_CARRY_SHOPS",

  // Popular Searches
  START_POPULAR_SEARCHES = "START_POPULAR_SEARCHES",
  SUCCESS_POPULAR_SEARCHES = "SUCCESS_POPULAR_SEARCHES",
  FAILURE_POPULAR_SEARCHES = "FAILURE_POPULAR_SEARCHES",

  // Search
  START_SEARCH = "START_SEARCH",
  SUCCESS_SEARCH = "SUCCESS_SEARCH",
  FAILURE_SEARCH = "FAILURE_SEARCH",

  // GetResponse check if subscribed
  START_GETRESPONSE_CHECK_SUBSCRIBED = "START_GETRESPONSE_CHECK_SUBSCRIBED",
  SUCCESS_GETRESPONSE_CHECK_SUBSCRIBED = "SUCCESS_GETRESPONSE_CHECK_SUBSCRIBED",
  FAILURE_GETRESPONSE_CHECK_SUBSCRIBED = "FAILURE_GETRESPONSE_CHECK_SUBSCRIBED",

  // GetResponse subscribe
  START_GETRESPONSE_SUBSCRIBE = "START_GETRESPONSE_SUBSCRIBE",
  SUCCESS_GETRESPONSE_SUBSCRIBE = "SUCCESS_GETRESPONSE_SUBSCRIBE",
  FAILURE_GETRESPONSE_SUBSCRIBE = "FAILURE_GETRESPONSE_SUBSCRIBE",

  // Contact Form get token
  START_CONTACT_FORM_GET_TOKEN = "START_CONTACT_FORM_GET_TOKEN",
  SUCCESS_CONTACT_FORM_GET_TOKEN = "SUCCESS_CONTACT_FORM_GET_TOKEN",
  FAILURE_CONTACT_FORM_GET_TOKEN = "FAILURE_CONTACT_FORM_GET_TOKEN",

  // Contact Form send message
  START_CONTACT_FORM_SEND_MESSAGE = "START_CONTACT_FORM_SEND_MESSAGE",
  SUCCESS_CONTACT_FORM_SEND_MESSAGE = "SUCCESS_CONTACT_FORM_SEND_MESSAGE",
  FAILURE_CONTACT_FORM_SEND_MESSAGE = "FAILURE_CONTACT_FORM_SEND_MESSAGE",
}

export type GlobalState = {
  // Auth
  isAuthenticated: boolean;
  authLoading: boolean;
  authError: string | null;

  menuItems: MenuItem[];
  linkBlocks: LinkBlock[];
  popularCategories: PopularCategory[];
  homepageImageSlider: {
    [key: string]: HomepageSlide[];
  };
  productsGroups: {
    [key: string]: ProductGroup[];
  };
  homepageCategories: {
    [key: string]: HomepageCategory[];
  };
  featuredProducts: {
    [key: string]: Product[];
  };
  loadingHomepage: boolean;
  errorHomepage: string | null;

  // Carry Shops
  carryShops: CarryShop[];
  loadingCarryShops: boolean;
  errorCarryShops: string | null;

  // Popular Searches
  popularSearches: PopularSearch[];
  matchingCategories: PopularSearchCategory[];
  loadingPopularSearches: boolean;
  errorPopularSearches: string | null;

  // Search
  searchResults: Product[];
  pagination: Pagination | null;
  filterData: FilterData | null;
  sortOrders: SortOrderFormatted[];
  loadingSearch: boolean;
  loadingMore: boolean;
  errorSearch: string | null;

  // GetResponse check if subscribed
  getResponseDataChecked: boolean;
  getResponseData: GetResponse | null;
  loadingGetResponseCheckSubscribed: boolean;
  errorGetResponseCheckSubscribed: string | null;

  // GetResponse subscribe
  loadingGetResponseSubscribe: boolean;
  errorGetResponseSubscribe: string | null;

  // Contact Form get token
  contactFormToken: string | null;
  loadingContactFormGetToken: boolean;
  errorContactFormGetToken: string | null;

  // Contact Form send message
  loadingContactFormSendMessage: boolean;
  errorContactFormSendMessage: string | null;
};

const GlobalStateContext = createContext<GlobalState | undefined>(undefined);

type GlobalAction =
  // Auth
  | { type: GlobalActionType.START_SIMPLE_AUTH }
  | { type: GlobalActionType.SUCCESS_SIMPLE_AUTH }
  | { type: GlobalActionType.FAILURE_SIMPLE_AUTH; error: string }
  | { type: GlobalActionType.LOGOUT }

  // Globals
  | { type: GlobalActionType.START_HOMEPAGE }
  | {
      type: GlobalActionType.SUCCESS_HOMEPAGE;
      linkBlocks: LinkBlock[];
      popularCategories: PopularCategory[];
      menuItems: MenuItem[];
      homepageImageSlider: {
        [key: string]: HomepageSlide[];
      };
      productsGroups: {
        [key: string]: ProductGroup[];
      };
      homepageCategories: {
        [key: string]: HomepageCategory[];
      };
      featuredProducts: {
        [key: string]: Product[];
      };
    }
  | { type: GlobalActionType.FAILURE_HOMEPAGE; error: string }

  // Carry Shops
  | { type: GlobalActionType.START_CARRY_SHOPS }
  | { type: GlobalActionType.SUCCESS_CARRY_SHOPS; carryShops: CarryShop[] }
  | { type: GlobalActionType.FAILURE_CARRY_SHOPS; error: string }

  // Popular Searches
  | { type: GlobalActionType.START_POPULAR_SEARCHES }
  | {
      type: GlobalActionType.SUCCESS_POPULAR_SEARCHES;
      popularSearches: PopularSearch[];
      matchingCategories: PopularSearchCategory[];
    }
  | { type: GlobalActionType.FAILURE_POPULAR_SEARCHES; error: string }

  // Search
  | { type: GlobalActionType.START_SEARCH; page: number }
  | {
      type: GlobalActionType.SUCCESS_SEARCH;
      searchResults: Product[];
      pagination: Pagination | null;
      page: number;
      filterData: FilterData | null;
      sortOrders: SortOrderFormatted[];
    }
  | { type: GlobalActionType.FAILURE_SEARCH; error: string; page: number }

  // GetResponse check if subscribed
  | { type: GlobalActionType.START_GETRESPONSE_CHECK_SUBSCRIBED }
  | {
      type: GlobalActionType.SUCCESS_GETRESPONSE_CHECK_SUBSCRIBED;
      getResponseData: GetResponse | null;
    }
  | {
      type: GlobalActionType.FAILURE_GETRESPONSE_CHECK_SUBSCRIBED;
      error: string;
    }

  // GetResponse subscribe
  | { type: GlobalActionType.START_GETRESPONSE_SUBSCRIBE }
  | {
      type: GlobalActionType.SUCCESS_GETRESPONSE_SUBSCRIBE;
      getResponseData: GetResponse | null;
    }
  | { type: GlobalActionType.FAILURE_GETRESPONSE_SUBSCRIBE; error: string }

  // Contact Form get token
  | { type: GlobalActionType.START_CONTACT_FORM_GET_TOKEN }
  | {
      type: GlobalActionType.SUCCESS_CONTACT_FORM_GET_TOKEN;
      contactFormToken: string;
    }
  | { type: GlobalActionType.FAILURE_CONTACT_FORM_GET_TOKEN; error: string }

  // Contact Form send message
  | { type: GlobalActionType.START_CONTACT_FORM_SEND_MESSAGE }
  | { type: GlobalActionType.SUCCESS_CONTACT_FORM_SEND_MESSAGE }
  | { type: GlobalActionType.FAILURE_CONTACT_FORM_SEND_MESSAGE; error: string };

type GlobalDispatch = Dispatch<GlobalAction>;

const GlobalDispatchContext = createContext<GlobalDispatch | undefined>(
  undefined,
);

const initialGlobalState: GlobalState = {
  // Simple Auth
  // apps/web/src/forms/SimpleAuthForm/SimpleAuthForm.tsx
  isAuthenticated: true,
  authLoading: true,
  authError: null,

  // Homepage
  menuItems: [],
  linkBlocks: [],
  popularCategories: [],
  homepageImageSlider: {},
  productsGroups: {},
  homepageCategories: {},
  featuredProducts: {},
  loadingHomepage: false,
  errorHomepage: null,

  // Carry Shops
  carryShops: [],
  loadingCarryShops: false,
  errorCarryShops: null,

  // Popular Searches
  popularSearches: [],
  matchingCategories: [],
  loadingPopularSearches: false,
  errorPopularSearches: null,

  // Search
  searchResults: [],
  pagination: null,
  filterData: null,
  sortOrders: [],
  loadingSearch: false,
  loadingMore: false,
  errorSearch: null,

  // GetResponse check if subscribed
  getResponseDataChecked: false,
  getResponseData: null,
  loadingGetResponseCheckSubscribed: false,
  errorGetResponseCheckSubscribed: null,

  // GetResponse subscribe
  loadingGetResponseSubscribe: false,
  errorGetResponseSubscribe: null,

  // Contact Form get token
  contactFormToken: null,
  loadingContactFormGetToken: false,
  errorContactFormGetToken: null,

  // Contact Form send message
  loadingContactFormSendMessage: false,
  errorContactFormSendMessage: null,
};

function globalReducer(state: GlobalState, action: GlobalAction): GlobalState {
  switch (action.type) {
    // Auth
    case GlobalActionType.START_SIMPLE_AUTH:
      return {
        ...state,
        authLoading: true,
        authError: null,
      };
    case GlobalActionType.SUCCESS_SIMPLE_AUTH:
      return {
        ...state,
        isAuthenticated: true,
        authLoading: false,
        authError: null,
      };
    case GlobalActionType.FAILURE_SIMPLE_AUTH:
      return {
        ...state,
        isAuthenticated: false,
        authLoading: false,
        authError: action.error,
      };
    case GlobalActionType.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        authLoading: false,
        authError: null,
      };

    // Homepage
    case GlobalActionType.START_HOMEPAGE:
      return {
        ...state,
        loadingHomepage: true,
      };
    case GlobalActionType.SUCCESS_HOMEPAGE:
      return {
        ...state,
        menuItems: action.menuItems,
        linkBlocks: action.linkBlocks,
        popularCategories: action.popularCategories,
        homepageImageSlider: action.homepageImageSlider,
        productsGroups: action.productsGroups,
        homepageCategories: action.homepageCategories,
        featuredProducts: action.featuredProducts,
        loadingHomepage: false,
        errorHomepage: null,
      };
    case GlobalActionType.FAILURE_HOMEPAGE:
      return {
        ...state,
        loadingHomepage: false,
        errorHomepage: action.error,
      };

    // Carry Shops
    case GlobalActionType.START_CARRY_SHOPS:
      return {
        ...state,
        loadingCarryShops: true,
      };
    case GlobalActionType.SUCCESS_CARRY_SHOPS:
      return {
        ...state,
        carryShops: action.carryShops,
        loadingCarryShops: false,
        errorCarryShops: null,
      };
    case GlobalActionType.FAILURE_CARRY_SHOPS:
      return {
        ...state,
        loadingCarryShops: false,
        errorCarryShops: action.error,
      };

    // Popular Searches
    case GlobalActionType.START_POPULAR_SEARCHES:
      return {
        ...state,
        loadingPopularSearches: true,
      };
    case GlobalActionType.SUCCESS_POPULAR_SEARCHES:
      return {
        ...state,
        popularSearches: action.popularSearches,
        matchingCategories: action.matchingCategories,
        loadingPopularSearches: false,
        errorPopularSearches: null,
      };
    case GlobalActionType.FAILURE_POPULAR_SEARCHES:
      return {
        ...state,
        loadingPopularSearches: false,
        errorPopularSearches: action.error,
      };

    // Search
    case GlobalActionType.START_SEARCH:
      if (action.page === 1) {
        return {
          ...state,
          loadingSearch: true,
          searchResults: [],
        };
      }

      return {
        ...state,
        loadingMore: true,
      };
    case GlobalActionType.SUCCESS_SEARCH:
      if (action.page === 1) {
        return {
          ...state,
          searchResults: action.searchResults,
          pagination: action.pagination,
          filterData: action.filterData,
          sortOrders: action.sortOrders,
          loadingSearch: false,
          errorSearch: null,
        };
      }

      // For pages > 1, append new results to existing ones
      return {
        ...state,
        searchResults: [...state.searchResults, ...action.searchResults],
        pagination: action.pagination,
        filterData: action.filterData,
        sortOrders: action.sortOrders,
        loadingMore: false,
        errorSearch: null,
      };
    case GlobalActionType.FAILURE_SEARCH:
      if (action.page === 1) {
        return {
          ...state,
          loadingSearch: false,
          errorSearch: action.error,
        };
      }

      return {
        ...state,
        loadingMore: false,
        errorSearch: action.error,
      };

    // GetResponse check if subscribed
    case GlobalActionType.START_GETRESPONSE_CHECK_SUBSCRIBED:
      return {
        ...state,
        loadingGetResponseCheckSubscribed: true,
      };
    case GlobalActionType.SUCCESS_GETRESPONSE_CHECK_SUBSCRIBED:
      return {
        ...state,
        getResponseDataChecked: true,
        getResponseData: action.getResponseData,
        loadingGetResponseCheckSubscribed: false,
        errorGetResponseCheckSubscribed: null,
      };
    case GlobalActionType.FAILURE_GETRESPONSE_CHECK_SUBSCRIBED:
      return {
        ...state,
        loadingGetResponseCheckSubscribed: false,
        errorGetResponseCheckSubscribed: action.error,
      };

    // GetResponse subscribe
    case GlobalActionType.START_GETRESPONSE_SUBSCRIBE:
      return {
        ...state,
        loadingGetResponseSubscribe: true,
      };
    case GlobalActionType.SUCCESS_GETRESPONSE_SUBSCRIBE:
      return {
        ...state,
        getResponseData: action.getResponseData,
        loadingGetResponseSubscribe: false,
        errorGetResponseSubscribe: null,
      };
    case GlobalActionType.FAILURE_GETRESPONSE_SUBSCRIBE:
      return {
        ...state,
        loadingGetResponseSubscribe: false,
        errorGetResponseSubscribe: action.error,
      };

    // Contact Form get token
    case GlobalActionType.START_CONTACT_FORM_GET_TOKEN:
      return {
        ...state,
        loadingContactFormGetToken: true,
      };
    case GlobalActionType.SUCCESS_CONTACT_FORM_GET_TOKEN:
      return {
        ...state,
        contactFormToken: action.contactFormToken,
        loadingContactFormGetToken: false,
        errorContactFormGetToken: null,
      };
    case GlobalActionType.FAILURE_CONTACT_FORM_GET_TOKEN:
      return {
        ...state,
        loadingContactFormGetToken: false,
        errorContactFormGetToken: action.error,
      };

    // Contact Form send message
    case GlobalActionType.START_CONTACT_FORM_SEND_MESSAGE:
      return {
        ...state,
        loadingContactFormSendMessage: true,
      };

    case GlobalActionType.SUCCESS_CONTACT_FORM_SEND_MESSAGE:
      return {
        ...state,
        loadingContactFormSendMessage: false,
        errorContactFormSendMessage: null,
      };
    case GlobalActionType.FAILURE_CONTACT_FORM_SEND_MESSAGE:
      return {
        ...state,
        loadingContactFormSendMessage: false,
        errorContactFormSendMessage: action.error,
      };

    default:
      throw new Error("Unhandled action");
  }
}

export function GlobalProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(globalReducer, initialGlobalState);

  // Sprawdź uwierzytelnienie przy inicjalizacji
  useEffect(() => {
    const checkAuth = () => {
      dispatch({ type: GlobalActionType.SUCCESS_SIMPLE_AUTH });
      if (state.getResponseDataChecked === false) {
        getGetResponseCheckSubscribedData(dispatch);
      }
    };

    checkAuth();
  }, []);

  return (
    <GlobalDispatchContext.Provider value={dispatch}>
      <GlobalStateContext.Provider value={useMemo(() => state, [state])}>
        {children}
      </GlobalStateContext.Provider>
    </GlobalDispatchContext.Provider>
  );
}

export function useGlobalState() {
  const state = useContext(GlobalStateContext);
  if (!state) throw new Error("GlobalStateContext not found");
  return state;
}

export function useGlobalDispatch() {
  const dispatch = useContext(GlobalDispatchContext);
  if (!dispatch) throw new Error("GlobalDispatchContext not found");
  return dispatch;
}

// Funkcje uwierzytelniania
const login = async (dispatch: GlobalDispatch, password: string) => {
  dispatch({ type: GlobalActionType.START_SIMPLE_AUTH });

  await new Promise((resolve) => setTimeout(resolve, 500));

  if (password === CORRECT_PASSWORD) {
    localStorage.setItem(AUTH_KEY, "true");
    dispatch({ type: GlobalActionType.SUCCESS_SIMPLE_AUTH });
    return true;
  } else {
    dispatch({
      type: GlobalActionType.FAILURE_SIMPLE_AUTH,
      error: "Nieprawidłowe hasło",
    });
    return false;
  }
};

const logout = (dispatch: GlobalDispatch) => {
  localStorage.removeItem(AUTH_KEY);
  dispatch({ type: GlobalActionType.LOGOUT });
};

const getHomepageData = async (dispatch: GlobalDispatch) => {
  dispatch({ type: GlobalActionType.START_HOMEPAGE });

  try {
    const response = await axiosInstance.get(`/rest${HomePageAPI}`);
    if (response.data.psdata) {
      const homepageData = snakeToCamelCaseObject(response.data.psdata);

      setTimeout(() => {
        dispatch({
          type: GlobalActionType.SUCCESS_HOMEPAGE,
          menuItems: homepageData.menuItems,
          linkBlocks: homepageData.linkBlocks,
          popularCategories: homepageData.popularCategories,
          homepageImageSlider: homepageData.homepageImageSlider,
          productsGroups: homepageData.productsGroups,
          homepageCategories: homepageData.homepageCategories,
          featuredProducts: homepageData.featuredProducts,
        });
      }, 1000);
    } else {
      dispatch({
        type: GlobalActionType.FAILURE_HOMEPAGE,
        error: response.data.error,
      });
    }
  } catch (error) {
    dispatch({
      type: GlobalActionType.FAILURE_HOMEPAGE,
      error: "Błąd podczas ładowania danych strony głównej " + error,
    });
  }
};

const getCarryShopsData = async (dispatch: GlobalDispatch) => {
  dispatch({ type: GlobalActionType.START_CARRY_SHOPS });

  try {
    const response = await axiosInstance.get(`/rest${CarryShopsAPI}`);

    if (response.data.psdata) {
      const carryShopsData = snakeToCamelCaseObject(response.data.psdata);

      setTimeout(() => {
        dispatch({
          type: GlobalActionType.SUCCESS_CARRY_SHOPS,
          carryShops: carryShopsData,
        });
      }, 1000);
    } else {
      dispatch({
        type: GlobalActionType.FAILURE_CARRY_SHOPS,
        error: response.data.error,
      });
    }
  } catch (error) {
    dispatch({
      type: GlobalActionType.FAILURE_CARRY_SHOPS,
      error: "Błąd podczas ładowania danych sklepów " + error,
    });
  }
};

const getPopularSearchesData = async (
  searchQuery: string,
  dispatch: GlobalDispatch,
) => {
  dispatch({ type: GlobalActionType.START_POPULAR_SEARCHES });

  try {
    const response = await axiosInstance.get(
      `/rest${PopularSearchesAPI}?s=${searchQuery}`,
    );

    if (response.data.psdata && response.data.code === 200) {
      const popularSearchesData = snakeToCamelCaseObject(response.data.psdata);

      setTimeout(() => {
        dispatch({
          type: GlobalActionType.SUCCESS_POPULAR_SEARCHES,
          popularSearches: popularSearchesData.popularSearches,
          matchingCategories: popularSearchesData.matchingCategories,
        });
      }, 1000);
    } else {
      dispatch({
        type: GlobalActionType.FAILURE_POPULAR_SEARCHES,
        error: response.data.error,
      });
    }
  } catch (error) {
    dispatch({
      type: GlobalActionType.FAILURE_POPULAR_SEARCHES,
      error: "Błąd podczas ładowania danych popularnych wyszukiwań " + error,
    });
  }
};

const getSearchData = async (
  searchQuery: string,
  page: number,
  category: "odziez-damska" | "odziez-meska" | "odziez-dziecieca" | "",
  filters: SearchFilters,
  dispatch: GlobalDispatch,
) => {
  dispatch({ type: GlobalActionType.START_SEARCH, page });

  try {
    const url = buildSearchUrl(searchQuery, page, category, filters, "search");
    const response = await axiosInstance.get(url);

    if (response.data.psdata && response.data.code === 200) {
      const searchData = snakeToCamelCaseObject(response.data.psdata);

      dispatch({
        type: GlobalActionType.SUCCESS_SEARCH,
        searchResults: searchData.products,
        pagination: searchData.pagination,
        page,
        filterData: searchData.filterData,
        sortOrders: searchData.sortOrders,
      });

      return searchData;
    }
  } catch (error) {
    dispatch({
      type: GlobalActionType.FAILURE_SEARCH,
      error: "Błąd podczas ładowania danych wyszukiwania " + error,
      page,
    });
  }
};

const getGetResponseCheckSubscribedData = async (dispatch: GlobalDispatch) => {
  dispatch({ type: GlobalActionType.START_GETRESPONSE_CHECK_SUBSCRIBED });

  try {
    const response = await axiosInstance.get(`/rest${GetResponseAPI}`);

    if (response.data.psdata && response.data.code === 200) {
      const getResponseData = snakeToCamelCaseObject(response.data.psdata);

      setTimeout(() => {
        dispatch({
          type: GlobalActionType.SUCCESS_GETRESPONSE_CHECK_SUBSCRIBED,
          getResponseData: getResponseData,
        });
      }, 2000);

      return { code: 200, data: getResponseData };
    } else {
      dispatch({
        type: GlobalActionType.FAILURE_GETRESPONSE_CHECK_SUBSCRIBED,
        error: response.data.error,
      });
    }
  } catch (error) {
    dispatch({
      type: GlobalActionType.FAILURE_GETRESPONSE_CHECK_SUBSCRIBED,
      error: "Błąd podczas ładowania danych GetResponse " + error,
    });
  }
};

const createGetResponseSubscribeData = async (
  email: string,
  dispatch: GlobalDispatch,
) => {
  dispatch({ type: GlobalActionType.START_GETRESPONSE_SUBSCRIBE });

  try {
    const response = await axiosInstance.post(
      `/rest${GetResponseAPI}?email=${email}`,
    );
    if (response.data.psdata && response.data.code === 200) {
      const getResponseData = snakeToCamelCaseObject(response.data.psdata);

      dispatch({
        type: GlobalActionType.SUCCESS_GETRESPONSE_SUBSCRIBE,
        getResponseData: getResponseData,
      });

      return { code: 200, data: getResponseData };
    } else {
      dispatch({
        type: GlobalActionType.FAILURE_GETRESPONSE_SUBSCRIBE,
        error: response.data.error,
      });

      return { code: 400, data: response.data.error };
    }
  } catch (error) {
    dispatch({
      type: GlobalActionType.FAILURE_GETRESPONSE_SUBSCRIBE,
      error: "Błąd podczas ładowania danych GetResponse " + error,
    });

    return {
      code: 400,
      data: "Błąd podczas ładowania danych GetResponse " + error,
    };
  }
};

const getContactFormTokenData = async (dispatch: GlobalDispatch) => {
  dispatch({ type: GlobalActionType.START_CONTACT_FORM_GET_TOKEN });

  try {
    const response = await axiosInstance.get(`/rest${ContactFormAPI}`);

    if (response.data.psdata && response.data.code === 200) {
      const contactFormTokenData = snakeToCamelCaseObject(response.data.psdata);

      dispatch({
        type: GlobalActionType.SUCCESS_CONTACT_FORM_GET_TOKEN,
        contactFormToken: contactFormTokenData.contactFormToken,
      });

      return { code: 200, token: contactFormTokenData };
    } else {
      dispatch({
        type: GlobalActionType.FAILURE_CONTACT_FORM_GET_TOKEN,
        error: response.data.error,
      });
    }
  } catch (error) {
    dispatch({
      type: GlobalActionType.FAILURE_CONTACT_FORM_GET_TOKEN,
      error: "Błąd podczas ładowania danych Contact Form " + error,
    });
  }
};

const sendContactFormMessageData = async (
  from: string,
  message: string,
  token: string,
  dispatch: GlobalDispatch,
) => {
  dispatch({ type: GlobalActionType.START_CONTACT_FORM_SEND_MESSAGE });

  try {
    const response = await axiosInstance.post(`/rest${ContactFormAPI}`, {
      from: from,
      message: message,
      token: token,
    });

    if (response.data.psdata && response.data.code === 200) {
      const contactFormMessageData = snakeToCamelCaseObject(
        response.data.psdata.messages,
      );

      dispatch({
        type: GlobalActionType.SUCCESS_CONTACT_FORM_SEND_MESSAGE,
      });

      return { code: 200, data: contactFormMessageData };
    } else {
      dispatch({
        type: GlobalActionType.FAILURE_CONTACT_FORM_SEND_MESSAGE,
        error: response.data.error,
      });
    }
  } catch (error) {
    dispatch({
      type: GlobalActionType.FAILURE_CONTACT_FORM_SEND_MESSAGE,
      error: "Błąd podczas ładowania danych Contact Form " + error,
    });
  }
};

export function useGlobal() {
  const state = useGlobalState();
  const dispatch = useGlobalDispatch();

  const loginFunc = (password: string) => login(dispatch, password);
  const logoutFunc = () => logout(dispatch);
  const getHomepageDataFunc = () => getHomepageData(dispatch);
  const getCarryShopsDataFunc = () => getCarryShopsData(dispatch);
  const getPopularSearchesDataFunc = (searchQuery: string) =>
    getPopularSearchesData(searchQuery, dispatch);
  const clearPopularSearchesDataFunc = () =>
    dispatch({
      type: GlobalActionType.SUCCESS_POPULAR_SEARCHES,
      popularSearches: [],
      matchingCategories: [],
    });

  const getSearchDataFunc = (
    searchQuery: string,
    page: number,
    category: "odziez-damska" | "odziez-meska" | "odziez-dziecieca" | "",
    filters: SearchFilters,
  ) => getSearchData(searchQuery, page, category, filters, dispatch);

  const clearSearchDataFunc = () =>
    dispatch({
      type: GlobalActionType.SUCCESS_SEARCH,
      searchResults: [],
      pagination: null,
      page: 1,
      filterData: null,
      sortOrders: [],
    });

  const getGetResponseCheckSubscribedDataFunc = () =>
    getGetResponseCheckSubscribedData(dispatch);

  const createGetResponseSubscribeDataFunc = (email: string) =>
    createGetResponseSubscribeData(email, dispatch);

  const clearGetResponseDataFunc = () =>
    dispatch({
      type: GlobalActionType.SUCCESS_GETRESPONSE_CHECK_SUBSCRIBED,
      getResponseData: null,
    });

  // Contact Form get token
  const getContactFormTokenDataFunc = () => getContactFormTokenData(dispatch);
  const sendContactFormMessageDataFunc = (
    from: string,
    message: string,
    token: string,
  ) => sendContactFormMessageData(from, message, token, dispatch);

  return {
    state,
    login: loginFunc,
    logout: logoutFunc,
    getHomepageData: getHomepageDataFunc,
    getCarryShopsData: getCarryShopsDataFunc,
    getPopularSearchesData: getPopularSearchesDataFunc,
    clearPopularSearchesData: clearPopularSearchesDataFunc,
    getSearchData: getSearchDataFunc,
    clearSearchData: clearSearchDataFunc,
    getGetResponseCheckSubscribedData: getGetResponseCheckSubscribedDataFunc,
    createGetResponseSubscribeData: createGetResponseSubscribeDataFunc,
    clearGetResponseData: clearGetResponseDataFunc,
    getContactFormTokenData: getContactFormTokenDataFunc,
    sendContactFormMessageData: sendContactFormMessageDataFunc,
  };
}
